import { useEffect, useState } from 'react';
import { CircleMarker, GeoJSON, Tooltip, useMap } from 'react-leaflet';
import type { Feature, Geometry } from 'geojson';
import MapShell from '../map/MapShell';
import CountryRiskLayer, { type ViewMode } from '../map/CountryRiskLayer';

// The console's map. Merges the two ZoneMaps that had drifted apart: the briefing's
// (risco/anomalia toggle, fire-blind) and the nowcast's (fires, anomaly-blind). One
// component takes both, so a municipal user sees today's grid and what is burning near
// them on the same picture, which is how they actually think about the day.

export interface MapFire {
  numero: string;
  lat: number;
  lon: number;
  natureza: string | null;
  estado_agrupado: string | null;
  concelho_anepc: string | null;
  distance_km?: number | null;
}

const isActive = (f: MapFire) =>
  f.estado_agrupado === 'Em Curso' || f.estado_agrupado === 'Em Despacho';

function FitZone({
  bbox,
  fires,
  fitFires,
}: {
  bbox: [number, number, number, number];
  fires: MapFire[];
  fitFires: boolean;
}) {
  const map = useMap();
  useEffect(() => {
    // bbox is [minLon, minLat, maxLon, maxLat] (concelhos_static.json)
    let [minLon, minLat, maxLon, maxLat] = bbox;
    // Only widen out to distant fires when explicitly asked. Fitting them by default
    // shrank the concelho into a corner of the frame to accommodate a fire 20 km away,
    // which is backwards: this map is the municipality's own territory first. The ring
    // gets its own view on the Fogos tab.
    if (fitFires) {
      for (const f of fires) {
        minLat = Math.min(minLat, f.lat);
        maxLat = Math.max(maxLat, f.lat);
        minLon = Math.min(minLon, f.lon);
        maxLon = Math.max(maxLon, f.lon);
      }
    }
    map.fitBounds(
      [
        [minLat, minLon],
        [maxLat, maxLon],
      ],
      { padding: [16, 16] },
    );
  }, [map, bbox, fires, fitFires]);
  return null;
}

interface ShapeDoc {
  slug: string;
  municipio: Geometry;
  freguesias: { nome: string; dtmnfr: string; geometry: Geometry }[];
}

/** CAOP outlines: the concelho border, and the freguesia borders inside it.
 *
 * Without this the console was a grid of dots with no edges — a blob, not a territory.
 * A GTF or SMPC coordinator knows their boundary by heart and reads its absence
 * instantly. Static artifact, baked by scripts/bake_concelho_shapes.py.
 */
function Outlines({ shape }: { shape: ShapeDoc }) {
  return (
    <>
      {shape.freguesias.map((f) => (
        <GeoJSON
          key={f.dtmnfr}
          data={{ type: 'Feature', geometry: f.geometry, properties: { nome: f.nome } } as Feature}
          style={{ color: '#94a3b8', weight: 0.8, opacity: 0.35, fill: false }}
        >
          <Tooltip sticky>
            <span className="font-mono text-xs">{f.nome}</span>
          </Tooltip>
        </GeoJSON>
      ))}
      <GeoJSON
        data={{ type: 'Feature', geometry: shape.municipio, properties: {} } as Feature}
        style={{ color: '#e2e8f0', weight: 2, opacity: 0.9, fill: false }}
        interactive={false}
      />
    </>
  );
}

export default function MunicipioMap({
  slug,
  bbox,
  cells,
  viewMode,
  fires = [],
  fitFires = false,
  height = 'h-[26rem]',
}: {
  slug: string;
  bbox: [number, number, number, number];
  cells: number[][];
  viewMode: ViewMode;
  fires?: MapFire[];
  fitFires?: boolean;
  height?: string;
}) {
  const [shape, setShape] = useState<ShapeDoc | null>(null);

  useEffect(() => {
    const cdn = import.meta.env.VITE_DATA_URL;
    if (!cdn) return;
    let cancelled = false;
    setShape(null);
    fetch(`${cdn}/concelho_shape/${slug}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: ShapeDoc) => !cancelled && setShape(d))
      .catch(() => undefined); // no shape yet: the map still works, just without edges
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className={`${height} overflow-hidden rounded-lg border border-ink-700`}>
      <MapShell
        tileUrl="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        tileAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        zoomControl={false}
        className="h-full w-full"
      >
        <FitZone bbox={bbox} fires={fires} fitFires={fitFires} />
        <CountryRiskLayer cells={cells} viewMode={viewMode} />
        {shape && <Outlines shape={shape} />}
        {fires.map((f) => (
          <CircleMarker
            key={f.numero}
            center={[f.lat, f.lon]}
            radius={isActive(f) ? 9 : 7}
            pathOptions={{
              color: '#F26A4B',
              weight: 2,
              fillColor: '#F26A4B',
              fillOpacity: isActive(f) ? 0.9 : 0.45,
            }}
          >
            <Tooltip>
              <span className="font-mono text-xs">
                {f.concelho_anepc ?? '?'}
                {f.natureza ? ` · ${f.natureza}` : ''}
                {f.estado_agrupado ? ` · ${f.estado_agrupado}` : ''}
                {f.distance_km != null ? ` · a ${f.distance_km.toFixed(1)} km` : ''}
              </span>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapShell>
    </div>
  );
}
